import React from 'react';
import styled from 'styled-components';
import moment from 'moment';

import { useLanguage } from '../../Context/LanguageContext';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import BillingInfoModal from '../../Model/Individual/MySettingModel/BillingInfoModal.js';

// subscriptionState → 좌측 구독 카드 표시 정책 //
// 칩은 피그마와 동일하게 subscribed(LIVE·정기구독) / 미구독(No Active Subscription) 2분기만. 세부 표기는 아래 플래그로 분기 //
// subscribed: 구독중 여부(칩 문구·색) / showPayment: 결제수단·가격 노출 / showFreeEnd: 무료 만료일 노출 //
const STATE_VIEW = {
  AMBASSADOR:     { subscribed: true,  showPayment: false, showFreeEnd: true  },
  FREE_COUPON:    { subscribed: true,  showPayment: false, showFreeEnd: true  },
  PAID_ACTIVE:    { subscribed: true,  showPayment: true,  showFreeEnd: false },
  PAID_SUSPENDED: { subscribed: true,  showPayment: true,  showFreeEnd: false },
  EXPIRED:        { subscribed: false, showPayment: false, showFreeEnd: false },
  TERMINATED:     { subscribed: false, showPayment: false, showFreeEnd: false },
  STARTING:       { subscribed: false, showPayment: false, showFreeEnd: false },
  NONE:           { subscribed: false, showPayment: false, showFreeEnd: false },
};

export default function BillingBox(props) {
  const {
    subscribeList,
    subscriptionSummary,
    onClickManage,
    onClickRegister,
    isViewBillingInfoModal,
    onCloseBillingInfoModal
  } = props;

  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  // 좌측 카드 표시값 — /api2/billing/subscription_summary 응답에서 파생 (design §5) //
  // 상태 판정은 백엔드가 내려주는 명시 필드(subscriptionState)로 직접 분기. summary 없음(204/null) → NONE 폴백 //
  const summary = subscriptionSummary;
  const state = summary?.subscriptionState ?? 'NONE';
  const view = STATE_VIEW[state] ?? STATE_VIEW.NONE;

  const itemName = summary?.itemName ?? '';
  const billingCycle = summary?.billingCycle ?? '';
  const subscribePrice = summary?.price ?? 0;
  const isAutoPayActive = summary?.autoPaymentEnabled ?? false;
  const remainingDays = summary?.remainingDays ?? 0;
  // 다음 결제일 센티넬(9999-12-31) — 무제한/예정일 없음으로 고정 //
  const isSentinelDate = (summary?.nextApprovalDate ?? '').startsWith('9999-12-31');
  const nextPaymentDate = (summary?.nextApprovalDate && !isSentinelDate) ? moment(summary.nextApprovalDate).format('YYYY.MM.DD') : '';
  const freeCouponEndDate = summary?.freeCouponEndDate ?? '';

  const hasCard = (summary?.cardName ?? '') !== '' && (summary?.cardNum ?? '') !== '';
  const paymentMethod = hasCard ? summary.cardName + ': ' + summary.cardNum : t('myStoreSetting.billing.noPaymentMethod');

  // 무료 매장 만료일 — 빈 값이면 무제한(엠버서더), 있으면 종료일 표기. 센티넬 날짜도 무제한 처리 //
  const isUnlimited = isSentinelDate || (view.showFreeEnd && freeCouponEndDate === '');
  const freeEndText = freeCouponEndDate === '' ? t('myStoreSetting.billing.unlimited') : moment(freeCouponEndDate).format('YYYY.MM.DD');

  // 칩 색상 — 구독중(primary) / 미구독(red) 2분기 //
  const TONE_COLOR = {
    subscribed:   { border: theme.primary, background: theme.primaryAlpha20, dot: theme.primary },
    unsubscribed: { border: theme.pointRed, background: theme.pointRedAlpha20, dot: theme.pointRed },
  };
  const chipTone = TONE_COLOR[view.subscribed ? 'subscribed' : 'unsubscribed'];

  // 헤더 버튼 — 카드 보유 여부로만 분기. 카드 있음 → 관리 / 없음 → 등록하기 //
  const onClickCta = hasCard ? onClickManage : onClickRegister;

  // 가격 포맷팅 — 1,000 단위 콤마 //
  function formatAmount(price) {
    return '₩ ' + price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // 헤더 버튼 라벨 결정 — 카드 있음 → 관리 / 없음 → 등록하기 //
  function getCtaLabel() {
    if (!hasCard) return t('myStoreSetting.billing.register');

    return t('myStoreSetting.billing.manage');
  }

  // 상품 타이틀 결정 //
  function getProductTitle() {
    if (state === 'AMBASSADOR') return 'Link-Zone Ambassador';

    if (!view.subscribed) return t('myStoreSetting.billing.notRegistered');

    return itemName !== '' ? itemName : t('myStoreSetting.billing.notRegistered');
  }

  // 상태 칩 렌더 — 구독중(LIVE·정기구독) / 미구독(No Active Subscription) 2분기 //
  function renderStatusChip() {
    if (view.subscribed) {
      return (
        <StatusChip borderColor={chipTone.border} backgroundColor={chipTone.background}>
          <ChipDot backgroundColor={chipTone.dot} />
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('myStoreSetting.billing.chipLive')}</Text>
          <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>·</Text>
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('myStoreSetting.billing.chipRegular')}</Text>
        </StatusChip>
      );
    }

    return (
      <StatusChip borderColor={chipTone.border} backgroundColor={chipTone.background}>
        <ChipDot backgroundColor={chipTone.dot} />
        <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('myStoreSetting.billing.chipNoActive')}</Text>
      </StatusChip>
    );
  }

  // 상품 메타 행 렌더 — 유료(주기·가격·자동결제), 무료(FREE), 미등록(안내) //
  function renderProductMeta() {
    if (view.showPayment) {
      return (
        <ProductMetaRow>
          <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText}>{billingCycle}</Text>
          <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText}>·</Text>
          <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText}>{formatAmount(subscribePrice)}</Text>
          {
            isAutoPayActive &&
            <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText}>{'· ' + t('myStoreSetting.billing.autoPayActive')}</Text>
          }
        </ProductMetaRow>
      );
    }

    if (view.showFreeEnd) {
      return (
        <ProductMetaRow>
          <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText}>FREE</Text>
        </ProductMetaRow>
      );
    }

    return (
      <ProductMetaRow>
        <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText}>{t('myStoreSetting.billing.noRegisteredInfo')}</Text>
      </ProductMetaRow>
    );
  }

  // 남은 기간 값 렌더 — 무제한 또는 N일 //
  function renderRemaining() {
    if (isUnlimited) {
      return (
        <MetaValueRow>
          <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{t('myStoreSetting.billing.unlimited')}</Text>
        </MetaValueRow>
      );
    }

    return (
      <MetaValueRow>
        <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{remainingDays}</Text>
        <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{t('myStoreSetting.billing.daySuffix')}</Text>
      </MetaValueRow>
    );
  }

  // 다음 결제 값 렌더 — 무제한 또는 예정일 표기 //
  function renderNextPayment() {
    if (isUnlimited) {
      return <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{t('myStoreSetting.billing.noScheduledDate')}</Text>;
    }

    return (
      <MetaValueRow>
        <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{nextPaymentDate}</Text>
        <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{t('myStoreSetting.billing.scheduled')}</Text>
      </MetaValueRow>
    );
  }

  // 우측 컬럼 렌더 — 구독 상태와 무관하게 카드 등록 시 결제수단 표시 (hasCard 기준) //
  function renderPaymentColumn() {
    return (
      <MetaColumnRight>
        <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{t('myStoreSetting.billing.paymentMethod')}</Text>
        <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{paymentMethod}</Text>
      </MetaColumnRight>
    );
  }

  // 결제 내역 행 렌더 //
  function renderHistoryRows() {
    if (subscribeList.length === 0) {
      return (
        <EmptyBox>
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subText}>{t('myStoreSetting.billing.noHistory')}</Text>
        </EmptyBox>
      );
    }

    return subscribeList.map((data, index) => (
      <HistoryRow key={'BillingHistoryRow-' + index} hoverColor={theme.primaryAlpha20}>
        <DateColumn>
          <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{moment(data.approvalDate).format(t('myStoreSetting.billing.dateFormat'))}</Text>
        </DateColumn>
        <DetailColumn>
          <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{data.itemName}</Text>
        </DetailColumn>
        <AmountColumn>
          <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{formatAmount(data.price)}</Text>
        </AmountColumn>
      </HistoryRow>
    ));
  }

  return (
    <TopLevelWrapper>
      {
        isViewBillingInfoModal &&
        <BillingInfoModal
          data={subscriptionSummary}
          onClickExit={onCloseBillingInfoModal} />
      }
      <SectionHeader>
        <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.primary}>{t('myStoreSetting.billing.sectionLabel')}</Text>
        <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.text}>{t('myStoreSetting.billing.sectionTitle')}</Text>
      </SectionHeader>
      <RowWrapper>
        <SubscribeCard backgroundColor={theme.subBackground}>
          <HeaderRow>
            {renderStatusChip()}
            <ManageButton
              borderColor={theme.primary}
              backgroundColor={theme.primaryAlpha20}
              onClick={onClickCta}>
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{getCtaLabel()}</Text>
            </ManageButton>
          </HeaderRow>
          <ProductInfoBox>
            <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.text}>{getProductTitle()}</Text>
            {renderProductMeta()}
          </ProductInfoBox>
          <SubscribeDivider backgroundColor={theme.line} />
          <MetaRow>
            <MetaColumn>
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{t('myStoreSetting.billing.remainingPeriod')}</Text>
              {renderRemaining()}
            </MetaColumn>
            <MetaColumn>
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{t('myStoreSetting.billing.nextPayment')}</Text>
              {renderNextPayment()}
            </MetaColumn>
            {renderPaymentColumn()}
          </MetaRow>
        </SubscribeCard>

        <HistoryCard backgroundColor={theme.subBackground}>
          <ColumnHeader>
            <DateColumn>
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('myStoreSetting.billing.columnApprovalDate')}</Text>
            </DateColumn>
            <DetailColumn>
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('myStoreSetting.billing.columnPaymentDetails')}</Text>
            </DetailColumn>
            <AmountColumn>
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('myStoreSetting.billing.columnPaymentAmount')}</Text>
            </AmountColumn>
          </ColumnHeader>
          <HistoryDivider backgroundColor={theme.line} />
          <ListWrapper scrollBarColor={theme.scrollBar}>
            {renderHistoryRows()}
          </ListWrapper>
        </HistoryCard>
      </RowWrapper>
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: stretch;

  margin-top: 36px;
  margin-bottom: 100px;

  gap: 15px;

  width: 100%;
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 10px;

  width: 100%;
`;

const RowWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: stretch;

  gap: 20px;

  width: 100%;
`;

const SubscribeCard = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: space-between;
  align-items: center;

  flex: 1 0 0;
  min-width: 0;

  padding: 20px;

  height: 207px;

  gap: 20px;

  border-radius: 10px;

  background-color: ${props => props.backgroundColor};

  transition: all 0.3s ease-in-out;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
`;

const StatusChip = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 5px;

  padding: 0 10px;
  height: 21px;

  border: 1px solid ${props => props.borderColor};
  border-radius: 100px;

  background-color: ${props => props.backgroundColor};

  transition: all 0.3s ease-in-out;
`;

const ChipDot = styled.div`
  width: 5px;
  height: 5px;

  border-radius: 100px;

  background-color: ${props => props.backgroundColor};

  transition: all 0.3s ease-in-out;
`;

const ManageButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 85px;
  height: 29px;

  border: 1px solid ${props => props.borderColor};
  border-radius: 5px;

  background-color: ${props => props.backgroundColor};

  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const ProductInfoBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  gap: 5px;

  width: 100%;
`;

const ProductMetaRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: 5px;
`;

const SubscribeDivider = styled.div`
  width: 100%;
  height: 1px;

  border-radius: 300px;

  background-color: ${props => props.backgroundColor};

  transition: all 0.3s ease-in-out;
`;

const MetaRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-end;

  gap: 30px;

  width: 100%;
`;

const MetaColumn = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  gap: 5px;
`;

const MetaValueRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-end;

  gap: 5px;
`;

const MetaColumnRight = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-end;

  flex: 1 0 0;
  min-width: 0;

  gap: 5px;
`;

const HistoryCard = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;

  flex: 1 0 0;
  min-width: 0;

  padding: 20px;

  height: 207px;

  gap: 7px;

  border-radius: 10px;

  background-color: ${props => props.backgroundColor};

  transition: all 0.3s ease-in-out;
`;

const ColumnHeader = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  padding: 0 22px 0 10px;

  width: calc(100% - 32px);
  height: 30px;
`;

const HistoryDivider = styled.div`
  margin: 0 10px;

  width: calc(100% - 20px);
  height: 1px;

  background-color: ${props => props.backgroundColor};

  opacity: 0.5;

  transition: all 0.3s ease-in-out;
`;

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: stretch;

  width: calc(100% - 10px);

  padding: 0 10px 0 0;

  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.scrollBarColor};

    border-radius: 50px;
  }

  &::-webkit-scrollbar-track {
    margin: 4px 0;

    background-color: transparent;

    border-radius: 300px;
  }

  &::-webkit-scrollbar-track-piece {
    background-color: transparent;
  }
`;

const HistoryRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  flex-shrink: 0;

  padding: 10px 0 10px 10px;

  width: calc(100% - 12px);

  border-radius: 5px;

  border: 1px solid transparent;

  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      border-color: ${props => props.hoverColor};
    }
  }
`;

const DateColumn = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  flex-shrink: 0;

  width: 130px;
`;

const DetailColumn = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  flex: 1 0 0;
  min-width: 0;

  overflow: hidden;
`;

const AmountColumn = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  flex-shrink: 0;

  margin-left: 10px;

  width: 100px;
`;

const EmptyBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 75px;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  margin: 0;

  color: ${props => props.color};

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  transition: all 0.3s ease-in-out;
`;
